import type { CandidateProfile, CandidateSubmission, CandidateStatus, Supporter } from "./types";

const candidateKey = "civicsignal:candidates";
const supporterKey = "civicsignal:supporters";

const now = () => new Date().toISOString();

const slugify = (value: string) =>
  value
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "");

const seedCandidates: CandidateProfile[] = [
  {
    id: "cand_maya",
    slug: "maya-thompson",
    name: "Maya Thompson",
    office: "City Council",
    district: "District 4",
    location: "Charlotte, NC",
    party: "Nonpartisan",
    committeeName: "Maya Thompson for Council",
    treasurerName: "Denise Carter",
    email: "maya@example.org",
    story:
      "Maya is running to make city services easier to reach, improve housing stability, and give neighborhood businesses a direct line into city hall.",
    quote: "I am running to make city services easier to reach and harder to ignore.",
    priorities: ["Housing", "Small business", "Public safety"],
    updates: [
      {
        id: "upd_maya_launch",
        title: "Campaign launch",
        body: "Our campaign is opening with neighborhood listening sessions and a volunteer signup drive.",
        date: "2026-06-12"
      }
    ],
    status: "approved",
    supporterCount: 1248,
    createdAt: "2026-06-01T12:00:00.000Z",
    updatedAt: "2026-06-12T12:00:00.000Z"
  },
  {
    id: "cand_andre",
    slug: "andre-williams",
    name: "Andre Williams",
    office: "County Commission",
    district: "At-Large",
    location: "Mecklenburg County, NC",
    party: "Democratic",
    committeeName: "Andre Williams for County Commission",
    treasurerName: "Lena Brooks",
    email: "andre@example.org",
    story:
      "Andre is focused on transit access, workforce pathways, and youth programs that meet families before problems become emergencies.",
    quote: "The campaign should feel like an open office, not a locked conference room.",
    priorities: ["Transit", "Workforce", "Youth programs"],
    updates: [
      {
        id: "upd_andre_listening",
        title: "Listening tour",
        body: "The campaign is collecting input from workers, parents, and small employers across the county.",
        date: "2026-06-10"
      }
    ],
    status: "pending_review",
    supporterCount: 612,
    createdAt: "2026-06-05T12:00:00.000Z",
    updatedAt: "2026-06-10T12:00:00.000Z"
  }
];

const readJson = <T,>(key: string, fallback: T): T => {
  try {
    const stored = window.localStorage.getItem(key);
    return stored ? (JSON.parse(stored) as T) : fallback;
  } catch {
    return fallback;
  }
};

const writeJson = <T,>(key: string, value: T) => {
  window.localStorage.setItem(key, JSON.stringify(value));
};

export const candidateStore = {
  list(): CandidateProfile[] {
    return readJson(candidateKey, seedCandidates);
  },
  saveAll(candidates: CandidateProfile[]) {
    writeJson(candidateKey, candidates);
  },
  submit(submission: CandidateSubmission): CandidateProfile {
    const timestamp = now();
    const candidate: CandidateProfile = {
      id: `cand_${crypto.randomUUID()}`,
      slug: slugify(submission.name),
      name: submission.name,
      office: submission.office,
      district: submission.district,
      location: submission.location,
      party: submission.party,
      committeeName: submission.committeeName,
      treasurerName: submission.treasurerName,
      email: submission.email,
      story: submission.story,
      quote: submission.quote,
      priorities: submission.priorities,
      updates: [
        {
          id: `upd_${crypto.randomUUID()}`,
          title: submission.updateTitle,
          body: submission.updateBody,
          date: timestamp.slice(0, 10)
        }
      ],
      status: "pending_review",
      supporterCount: 0,
      createdAt: timestamp,
      updatedAt: timestamp
    };
    const candidates = [candidate, ...this.list()];
    this.saveAll(candidates);
    return candidate;
  },
  update(candidate: CandidateProfile): CandidateProfile {
    const updated = { ...candidate, updatedAt: now() };
    this.saveAll(this.list().map((item) => (item.id === candidate.id ? updated : item)));
    return updated;
  },
  setStatus(id: string, status: CandidateStatus, reviewNote = "") {
    const candidates = this.list().map((candidate) =>
      candidate.id === id ? { ...candidate, status, reviewNote, updatedAt: now() } : candidate
    );
    this.saveAll(candidates);
  },
  reset() {
    this.saveAll(seedCandidates);
    writeJson(supporterKey, []);
  }
};

export const supporterStore = {
  list(): Supporter[] {
    return readJson(supporterKey, []);
  },
  follow(candidateId: string, supporter: Omit<Supporter, "id" | "candidateId" | "createdAt">) {
    const supporters = this.list();
    const record: Supporter = {
      id: `sup_${crypto.randomUUID()}`,
      candidateId,
      ...supporter,
      createdAt: now()
    };
    writeJson(supporterKey, [record, ...supporters]);
    const candidates = candidateStore.list().map((candidate) =>
      candidate.id === candidateId
        ? { ...candidate, supporterCount: candidate.supporterCount + 1, updatedAt: now() }
        : candidate
    );
    candidateStore.saveAll(candidates);
    return record;
  }
};
